import {PlusCircleOutlined} from '@ant-design/icons';
import {unwrapResult} from '@reduxjs/toolkit';
import {Button, Empty, Form} from 'antd';
import {useForm} from 'antd/es/form/Form';
import cn from 'classnames';
import {pick} from 'lodash-es';
import React, {
    useContext, useEffect, useState, FC,
} from 'react';

import {EErrorMessages} from 'shared/constants/messages';
import {setFieldsValueUntouched, showMessage} from 'shared/utils';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {
    selectTableReportConfigurationData,
} from 'store/slices/report-configuration-slice/report-configuration-slice';
import {
    updateTableReportConfiguration,
} from 'store/slices/report-configuration-slice/report-configuration/report-configuration-thunks';

import {EWatcherFieldFlag} from '../../hooks/use-form-fields-manager';
import {useFormListFieldsManager} from '../../hooks/use-form-list-fields-manager';
import {ReportConfigurationContext} from '../../report-configuration.context';
import {
    REPORT_PROPERTIES_SETTINGS_FORM_DATA_KEY,
    ReportPropertiesSettingsDefaultValueFields,
    IReportPropertiesSettingsDto,
    EReportPropertiesSettingsFormFields,
    TReportPropertySetting,
} from './constants/report-properties-settings';
import {ReportPropertiesSettingsButtons} from './properties-settings-buttons';
import {ReportPropertiesSettingFields} from './properties-settings-fields/report-properties-setting-fields';
import {getDataFromReportPropertiesSettingsRequest} from './utils/report-properties-settings.utils';

import './report-properties-settings.less';

export interface IReportPropertiesFormState {
    initialValues: Record<string, any>;
    isEditingForm: boolean;
    isDirty: boolean;
}

export interface IReportPropertiesProps {}

export const ReportPropertiesSettings: FC<IReportPropertiesProps> = () => {
    const [form] = useForm<IReportPropertiesSettingsDto>();
    const [formState, setFormState] = useState({initialValues: {}, isEditingForm: false, isDirty: false});
    const {
        templateCode,
    } = useContext(ReportConfigurationContext);

    const dispatch = useAppDispatch();

    const reportConfigurationData = useAppSelector(
        selectTableReportConfigurationData,
    );

    const {tableDatasourceType} = reportConfigurationData ?? {};

    const disabledByEditingModeCondition = {
        condition: () => !formState.isEditingForm,
        flag: EWatcherFieldFlag.isDisabled,
    };

    const {
        applyFieldWatchers,
        checkWatcherFlag,
        checkArrayIndependentWatcherFlag,
    } = useFormListFieldsManager<'queryParameters',
        Record<EReportPropertiesSettingsFormFields, string | boolean>,
        IReportPropertiesSettingsDto
    >({
        formInstance: form,
        watchers: {
            [EReportPropertiesSettingsFormFields.paramName]: [
                disabledByEditingModeCondition, {
                    condition: () => tableDatasourceType === 'TABLE',
                    flag: EWatcherFieldFlag.isHidden,
                },
                {
                    condition: () => ['FUNCTION', 'SQL'].includes(tableDatasourceType as string),
                    flag: EWatcherFieldFlag.isRequired,
                }],
            [EReportPropertiesSettingsFormFields.columnName]: [
                disabledByEditingModeCondition, {
                    condition: () => tableDatasourceType === 'TABLE',
                    flag: EWatcherFieldFlag.isRequired,
                }, {
                    condition: () => ['FUNCTION', 'SQL', undefined]
                        .includes(tableDatasourceType as string),
                    flag: EWatcherFieldFlag.isHidden,
                }],
            [EReportPropertiesSettingsFormFields.valuesetLookupType]: [
                disabledByEditingModeCondition,
                {
                    condition: ({formValues}) => !(['VALUESET', 'MULTIVALUESET']
                        .includes(formValues?.[EReportPropertiesSettingsFormFields.type]))
                        || (formValues?.[EReportPropertiesSettingsFormFields.defaultType] !== 'CONST'
                            && formValues?.[EReportPropertiesSettingsFormFields.defaultType] !== 'UNSET'),
                    flag: EWatcherFieldFlag.isHidden,
                },
            ],
            [EReportPropertiesSettingsFormFields.sqlValueset]: [
                disabledByEditingModeCondition,
                {
                    condition: ({formValues}) => !((formValues
                        ?.[EReportPropertiesSettingsFormFields.type] === 'SQLVALUESET' || formValues
                        ?.[EReportPropertiesSettingsFormFields.type] === 'SQLMULTIVALUESET')
                        && ['UNSET', 'CONST', undefined].includes(formValues
                            ?.[EReportPropertiesSettingsFormFields.defaultType])),
                    flag: EWatcherFieldFlag.isHidden,
                },
            ],
            [EReportPropertiesSettingsFormFields.defaultValueSqlValueSet]: [
                disabledByEditingModeCondition,
                {
                    condition: ({formValues}) => !((formValues
                        ?.[EReportPropertiesSettingsFormFields.type] === 'SQLVALUESET' || formValues
                        ?.[EReportPropertiesSettingsFormFields.type] === 'SQLMULTIVALUESET')
                        && formValues?.[EReportPropertiesSettingsFormFields.defaultType] === 'CONST'
                    ),
                    flag: EWatcherFieldFlag.isHidden,
                },
            ],
            [EReportPropertiesSettingsFormFields.defaultValueContext]: [
                disabledByEditingModeCondition,
                {
                    condition: (
                        {formValues},
                    ) => formValues?.[EReportPropertiesSettingsFormFields.defaultType] !== 'CONTEXT',
                    flag: EWatcherFieldFlag.isHidden,
                },
            ],
            [EReportPropertiesSettingsFormFields.defaultValueDate]: [
                disabledByEditingModeCondition,
                {
                    condition: ({formValues}) => formValues?.[EReportPropertiesSettingsFormFields.type] !== 'DATE'
                        || formValues?.[EReportPropertiesSettingsFormFields.defaultType] !== 'CONST',
                    flag: EWatcherFieldFlag.isHidden,
                },
            ],
            [EReportPropertiesSettingsFormFields.defaultValueDateTime]: [
                disabledByEditingModeCondition,
                {
                    condition: ({formValues}) => formValues?.[EReportPropertiesSettingsFormFields.type] !== 'DATETIME'
                        || formValues?.[EReportPropertiesSettingsFormFields.defaultType] !== 'CONST',
                    flag: EWatcherFieldFlag.isHidden,
                },
            ],
            [EReportPropertiesSettingsFormFields.defaultValueInteger]: [
                disabledByEditingModeCondition,
                {
                    condition: ({formValues}) => (formValues?.[EReportPropertiesSettingsFormFields.type] !== 'INTEGER'
                            && formValues?.[EReportPropertiesSettingsFormFields.type] !== 'NUMBER')
                        || formValues?.[EReportPropertiesSettingsFormFields.defaultType] !== 'CONST',
                    flag: EWatcherFieldFlag.isHidden,
                },
            ],
            [EReportPropertiesSettingsFormFields.defaultValueBoolean]: [
                disabledByEditingModeCondition,
                {
                    condition: ({formValues}) => formValues?.[EReportPropertiesSettingsFormFields.type] !== 'BOOLEAN'
                        || formValues?.[EReportPropertiesSettingsFormFields.defaultType] !== 'CONST',
                    flag: EWatcherFieldFlag.isHidden,
                },
            ],
            [EReportPropertiesSettingsFormFields.defaultValueString]: [
                disabledByEditingModeCondition,
                {
                    condition: ({formValues}) => formValues?.[EReportPropertiesSettingsFormFields.type] !== 'STRING'
                        || formValues?.[EReportPropertiesSettingsFormFields.defaultType] !== 'CONST',
                    flag: EWatcherFieldFlag.isHidden,
                },
            ],
            [EReportPropertiesSettingsFormFields.defaultValueValueSet]: [
                disabledByEditingModeCondition,
                {
                    condition: ({formValues}) => formValues?.[EReportPropertiesSettingsFormFields.type] !== 'VALUESET'
                        || formValues?.[EReportPropertiesSettingsFormFields.defaultType] !== 'CONST',
                    flag: EWatcherFieldFlag.isHidden,
                },
            ],
            [EReportPropertiesSettingsFormFields.defaultValueMultiValueSet]: [
                disabledByEditingModeCondition,
                {
                    condition: (
                        {formValues},
                    ) => formValues?.[EReportPropertiesSettingsFormFields.type] !== 'MULTIVALUESET'
                        || formValues?.[EReportPropertiesSettingsFormFields.defaultType] !== 'CONST',
                    flag: EWatcherFieldFlag.isHidden,
                },
            ],
            [EReportPropertiesSettingsFormFields.defaultValueDateRange]: [
                disabledByEditingModeCondition,
                {
                    condition: ({formValues}) => formValues?.[EReportPropertiesSettingsFormFields.type] !== 'VALUERANGE'
                        || formValues?.[EReportPropertiesSettingsFormFields.defaultType] !== 'CONST',
                    flag: EWatcherFieldFlag.isHidden,
                },
            ],
            [EReportPropertiesSettingsFormFields.format]: [
                disabledByEditingModeCondition,
                {
                    condition: ({formValues}) => !['DATE', 'DATETIME', 'NUMBER', 'INTEGER']
                        .includes(formValues?.[EReportPropertiesSettingsFormFields.type]),
                    flag: EWatcherFieldFlag.isHidden,
                },
            ],
            [EReportPropertiesSettingsFormFields.inputRestrictions]: [
                disabledByEditingModeCondition,
                {
                    condition: ({formValues}) => formValues?.[EReportPropertiesSettingsFormFields.type] !== 'VALUERANGE',
                    flag: EWatcherFieldFlag.isHidden,
                },
            ],
            [EReportPropertiesSettingsFormFields.periodLength]: [
                disabledByEditingModeCondition,
                {
                    condition: ({formValues}) => formValues?.[EReportPropertiesSettingsFormFields.type] !== 'VALUERANGE'
                        || !formValues?.[EReportPropertiesSettingsFormFields.inputRestrictions],
                    flag: EWatcherFieldFlag.isHidden,
                },
                {
                    condition: ({formValues}) => formValues?.[EReportPropertiesSettingsFormFields.type] === 'VALUERANGE'
                        && formValues?.[EReportPropertiesSettingsFormFields.inputRestrictions],
                    flag: EWatcherFieldFlag.isRequired,
                },
            ],
            [EReportPropertiesSettingsFormFields.defaultType]: [
                disabledByEditingModeCondition,
                {
                    condition: ({formValues}) => !['DATE', 'DATETIME', 'VALUERANGE']
                        .includes(formValues?.[EReportPropertiesSettingsFormFields.type]),
                    flag: EWatcherFieldFlag.shouldFilterLookup,
                },
            ],
            ...(() => Object.fromEntries([
                ...Object.values(pick(
                    EReportPropertiesSettingsFormFields, [
                        EReportPropertiesSettingsFormFields.type,
                        EReportPropertiesSettingsFormFields.visible,
                        EReportPropertiesSettingsFormFields.required,
                        EReportPropertiesSettingsFormFields.reportTitle,
                        EReportPropertiesSettingsFormFields.ordinal,
                    ],
                )),
            ].map(fieldName => [fieldName, [disabledByEditingModeCondition]])))(),
        },
        arrayIndependentWatchers: {
            buttonClearFormFields: [{
                isNotInput: true,
                condition: ({formValues}) => !formValues[REPORT_PROPERTIES_SETTINGS_FORM_DATA_KEY]
                    || !formValues[REPORT_PROPERTIES_SETTINGS_FORM_DATA_KEY]?.length
                    || !formState.isEditingForm,
                flag: EWatcherFieldFlag.isDisabled,
            }],
            addFormItemButton: [
                {
                    isNotInput: true,
                    condition: () => !formState.isEditingForm,
                    flag: EWatcherFieldFlag.isDisabled,
                },
            ],
            removeButton: [
                {
                    isNotInput: true,
                    condition: () => !formState.isEditingForm,
                    flag: EWatcherFieldFlag.isDisabled,
                },
            ],
        },
        triggerDeps: [formState.isEditingForm, tableDatasourceType],
        formArrayKey: REPORT_PROPERTIES_SETTINGS_FORM_DATA_KEY,
        modifiers: {
            trackFormDirtyState: true,
            isDirty: formState.isDirty,
            setIsDirty: isDirty => setFormState(v => ({...v, isDirty})),
        },
        performanceModifiers: {
            checkIfShouldSkip: changedValues => {
                const skipApplyWatchersFields = [
                    EReportPropertiesSettingsFormFields.paramName,
                    EReportPropertiesSettingsFormFields.reportTitle,
                    EReportPropertiesSettingsFormFields.ordinal,
                    EReportPropertiesSettingsFormFields.format,
                    EReportPropertiesSettingsFormFields.visible,
                    EReportPropertiesSettingsFormFields.required,
                    EReportPropertiesSettingsFormFields.defaultValueString,
                    EReportPropertiesSettingsFormFields.defaultValueInteger,
                ];
                return changedValues.queryParameters?.some(row => row && Object.keys(row)
                    .every(key => skipApplyWatchersFields.includes(key as EReportPropertiesSettingsFormFields)));
            },
        },
    });

    useEffect(() => {
        if (reportConfigurationData) {
            const {queryParameters = []} = reportConfigurationData ?? {};

            const dataToSetToForm = getDataFromReportPropertiesSettingsRequest(
                queryParameters,
            );

            form.resetFields();
            setFieldsValueUntouched(form, {[REPORT_PROPERTIES_SETTINGS_FORM_DATA_KEY]: dataToSetToForm});
            setFormState(v => ({...v, initialValues: {[REPORT_PROPERTIES_SETTINGS_FORM_DATA_KEY]: dataToSetToForm}}));
        }
        applyFieldWatchers();
    }, [reportConfigurationData]);

    const handleFinish = (values: any) => {
        const valuesConvertedForRequest = values.queryParameters.map((value: TReportPropertySetting) => {
            const [defaultValueIndex] = ReportPropertiesSettingsDefaultValueFields.filter(v => !!value[v]);
            if (!defaultValueIndex) return value;
            return {
                ...value,
                defaultValue: value[defaultValueIndex],
            };
        });

        if (templateCode) {
            dispatch(updateTableReportConfiguration({
                templateCode,
                data: {
                    queryParameters: valuesConvertedForRequest,
                },
            }))
                .then(unwrapResult)
                .catch(error => {
                    showMessage({
                        message: error?.response?.data ?? EErrorMessages.UNEXPECTED_ERROR_OCCURRED,
                        isError: true,
                    });
                    setFormState(v => ({...v, isEditingForm: true}));
                });
        }
    };

    return (
        <div className={cn('report-properties-settings')}>
            <Form
                layout="vertical"
                form={form}
                onFinish={handleFinish}
                // name={ReportConfigurationFormName.PROPERTIES_SETTINGS}
                onValuesChange={changedValues => {
                    applyFieldWatchers(changedValues);
                }}
                className={cn('report-properties-settings__form')}
            >
                <ReportPropertiesSettingsButtons {...{
                    formState,
                    setFormState,
                    form,
                    checkArrayIndependentWatcherFlag,
                    name: REPORT_PROPERTIES_SETTINGS_FORM_DATA_KEY,
                }}
                />
                <div className={cn('report-properties-settings__section-title')} />
                <div className={cn('report-properties-settings__body')}>
                    <Form.List name={REPORT_PROPERTIES_SETTINGS_FORM_DATA_KEY}>{(fields, {add, remove}) => (
                        <>
                            <Button
                                type="primary"
                                onClick={() => add()}
                                icon={(<PlusCircleOutlined className="svg-icon-size-16" />)}
                                disabled={checkArrayIndependentWatcherFlag('addFormItemButton',
                                    EWatcherFieldFlag.isDisabled)}
                            >Добавить
                            </Button>
                            {!fields.length && <Empty />}
                            <ReportPropertiesSettingFields
                                form={form}
                                fields={fields}
                                remove={remove}
                                checkWatcherFlag={checkWatcherFlag}
                                applyFieldWatchers={applyFieldWatchers}
                                checkArrayIndependentWatcherFlag={checkArrayIndependentWatcherFlag}
                                values={form?.getFieldsValue()[REPORT_PROPERTIES_SETTINGS_FORM_DATA_KEY]}
                            />
                        </>
                    )}
                    </Form.List>
                </div>
            </Form>
        </div>
    );
};
